import { type UIMessage, useChat } from "@ai-sdk/react";
import { MinusIcon, NotePencilIcon } from "@phosphor-icons/react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { HandraiseHGradient } from "@/assets/handraise";
import { useHeraldDebugToolCallsEnabled } from "@/components/herald-chat/dev-tools/heraldDebugToolCalls";
import { HeraldChatMessageList } from "@/components/herald-chat/HeraldChatMessageList";
import ChatErrorBoundary, {
  ChatErrorBanner,
} from "@/components/ui/chat/ChatErrorBoundary";
import ChatInput, {
  appendDictatedPhrase,
  notifyDictationTruncated,
} from "@/components/ui/chat/ChatInput";
import { ChatPromptSubmitContext } from "@/components/ui/chat/ChatPromptSubmitContext";
import { IconButton } from "@/components/ui/icon-button";
import { ResizeHandle } from "@/components/ui/resize-handle";
import type { FeedArticleProjectionFilterInput } from "@/data-access/shared/types/feed-article-projection.types";
import { useResizeHandle } from "@/hooks/useResizeHandle";
import useChatSubmit, {
  type SendableMessage,
} from "@/lib/hooks/chat/useChatSubmit";
import useChatTransport from "@/lib/hooks/chat/useChatTransport";
import { useStreamingQuipForLatestAssistant } from "@/lib/hooks/chat/useStreamingQuipForLatestAssistant";
import useVoiceToText, {
  type VoiceRecognitionErrorInfo,
  voiceRecognitionToastMessage,
} from "@/lib/hooks/chat/useVoiceToText";
import { cn } from "@/lib/utils/cn";
import useHeraldChatSettingsStore from "@/store/herald-chat/herald-chat-settings.slice";

const CHAT_PANEL_DEFAULT_WIDTH_PX = 400;
const CHAT_PANEL_MIN_WIDTH_PX = CHAT_PANEL_DEFAULT_WIDTH_PX;
const CHAT_PANEL_MAX_VIEWPORT_FRACTION = 0.5;
const CHAT_PANEL_MIN_PAGE_WIDTH_PX = 400;

const getViewportWidthPx = () =>
  typeof window !== "undefined" ? window.innerWidth : 1200;

const computeMaxChatPanelWidthPx = (viewport = getViewportWidthPx()) => {
  const marginPx = 16;
  const maxByViewport = viewport - marginPx;
  const maxFraction = Math.floor(viewport * CHAT_PANEL_MAX_VIEWPORT_FRACTION);
  return Math.min(maxByViewport, maxFraction);
};

const initialChatPanelWidthPx = () => {
  const maxPx = computeMaxChatPanelWidthPx();
  const resizeFloorPx = Math.min(CHAT_PANEL_MIN_WIDTH_PX, maxPx);
  return Math.max(resizeFloorPx, Math.min(CHAT_PANEL_DEFAULT_WIDTH_PX, maxPx));
};

const EMPTY_UI_MESSAGES: UIMessage[] = [];

export interface InAppChatShellProps {
  /** Page layout that narrows around Chat while space is available. */
  children: ReactNode;
  /** Content column used to detect when Chat must become an overlay. */
  overlayContentRef?: RefObject<HTMLElement | null>;
  /** Pass `"analysis"` to send analysis `mode` on generate requests. */
  mode?: "analysis";
  filterContext?: FeedArticleProjectionFilterInput | null;
  showOpenChatFab?: boolean;
}

/** Primary content with a chat column that overlays before content becomes too narrow. */
const InAppChatShell = ({
  children,
  overlayContentRef,
  mode,
  filterContext,
  showOpenChatFab = true,
}: InAppChatShellProps) => {
  const [open, setOpen] = useState(false);
  const [chatPanelWidthPx, setChatPanelWidthPx] = useState(
    initialChatPanelWidthPx,
  );
  const [viewportWidthPx, setViewportWidthPx] = useState(getViewportWidthPx);
  const maxChatPanelWidthPx = computeMaxChatPanelWidthPx(viewportWidthPx);
  const [shouldOverlayChat, setShouldOverlayChat] = useState(false);
  const [chatInstanceId] = useState(() => `in-app-${crypto.randomUUID()}`);
  const [chatErrorBannerKey, setChatErrorBannerKey] = useState(0);

  useEffect(() => {
    const syncViewportCap = () => {
      const nextViewportWidthPx = window.innerWidth;
      const nextMax = computeMaxChatPanelWidthPx(nextViewportWidthPx);
      setViewportWidthPx(nextViewportWidthPx);
      const resizeFloorPx = Math.min(CHAT_PANEL_MIN_WIDTH_PX, nextMax);
      setChatPanelWidthPx((previous) =>
        Math.max(resizeFloorPx, Math.min(previous, nextMax)),
      );
    };
    window.addEventListener("resize", syncViewportCap);
    return () => window.removeEventListener("resize", syncViewportCap);
  }, []);

  const resizeMinWidthPx = Math.min(
    CHAT_PANEL_MIN_WIDTH_PX,
    maxChatPanelWidthPx,
  );

  useLayoutEffect(() => {
    const content = overlayContentRef?.current;
    if (!open || !content) {
      setShouldOverlayChat(false);
      return;
    }

    const syncOverlay = () => {
      setShouldOverlayChat((currentlyOverlays) => {
        const measuredWidthPx = content.getBoundingClientRect().width;
        const inFlowWidthPx =
          measuredWidthPx - (currentlyOverlays ? chatPanelWidthPx : 0);
        return inFlowWidthPx < CHAT_PANEL_MIN_PAGE_WIDTH_PX;
      });
    };

    syncOverlay();
    const resizeObserver = new ResizeObserver(syncOverlay);
    resizeObserver.observe(content);
    return () => resizeObserver.disconnect();
  }, [chatPanelWidthPx, open, overlayContentRef]);

  const {
    handleMouseDown: handleResizeMouseDown,
    handleKeyDown: handleResizeKeyDown,
    isResizing,
  } = useResizeHandle({
    minWidth: resizeMinWidthPx,
    maxWidth: maxChatPanelWidthPx,
    getCurrentWidth: () => chatPanelWidthPx,
    onWidthChange: setChatPanelWidthPx,
    invertDelta: true,
  });

  const {
    transport,
    resetConversation,
    conversationId: capturedConversationId,
  } = useChatTransport({
    mode,
    filterContext,
  });

  const {
    messages,
    sendMessage,
    setMessages,
    status,
    stop,
    error,
    regenerate,
    clearError,
  } = useChat({
    id: chatInstanceId,
    transport,
    messages: EMPTY_UI_MESSAGES,
    throttle: 100,
  });

  useEffect(() => {
    if (error != null) {
      setChatErrorBannerKey((key) => key + 1);
    }
  }, [error]);

  const isStreaming = status === "streaming" || status === "submitted";

  const {
    input,
    setInput,
    files,
    setFiles,
    inputRef,
    handleSubmit,
    handleNewThread,
  } = useChatSubmit({
    isStreaming,
    sendMessage: sendMessage as (message: SendableMessage) => void,
    setMessages,
    resetConversation,
  });

  const isDeepAnalysisEnabled = useHeraldChatSettingsStore(
    (state) => state.isDeepAnalysisEnabled,
  );
  const toggleDeepAnalysis = useHeraldChatSettingsStore(
    (state) => state.toggleDeepAnalysis,
  );

  const handleBriefingModeChange = useCallback(
    (briefingMode: "briefing" | "deep") => {
      const wantDeep = briefingMode === "deep";
      if (wantDeep !== isDeepAnalysisEnabled) toggleDeepAnalysis();
    },
    [isDeepAnalysisEnabled, toggleDeepAnalysis],
  );

  const lastMessage = messages[messages.length - 1];
  const lastVisibleAssistantId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index];
      if (message != null && message.role === "assistant") {
        return message.id;
      }
    }
    return null;
  }, [messages]);

  const showPendingThinking =
    (status === "streaming" || status === "submitted") &&
    lastMessage != null &&
    lastMessage.role === "user";

  const showDevToolCalls = useHeraldDebugToolCallsEnabled();

  const streamingQuipMessage = useStreamingQuipForLatestAssistant({
    messages,
    lastVisibleAssistantId,
    isStreaming,
    showPendingThinking,
    showDevToolCalls,
  });

  const handleBannerRetry = useCallback(async () => {
    if (!lastVisibleAssistantId) return;
    clearError();
    try {
      await regenerate({ messageId: lastVisibleAssistantId });
    } catch (err) {
      console.error("[InAppChatShell handleBannerRetry]", err);
    }
  }, [clearError, regenerate, lastVisibleAssistantId]);

  const { isRecording, isSupported, startListening, stopListening } =
    useVoiceToText({
      shouldAbort: isStreaming,
      onFinalPhrase: useCallback(
        (phrase: string) => {
          let truncated = false;
          setInput((prev) => {
            const result = appendDictatedPhrase(prev, phrase);
            truncated = result.truncated;
            return result.value;
          });
          if (truncated) notifyDictationTruncated();
        },
        [setInput],
      ),
      onError: useCallback((info: VoiceRecognitionErrorInfo) => {
        const msg = voiceRecognitionToastMessage(info);
        if (msg) toast.error(msg);
      }, []),
    });

  const handleStop = useCallback(() => {
    if (isRecording) {
      stopListening();
      return;
    }
    void stop();
  }, [isRecording, stopListening, stop]);

  const handleMicPress = useCallback(() => {
    if (!isSupported) {
      toast.info("Dictation is not supported in this browser.");
      return;
    }
    startListening();
  }, [isSupported, startListening]);

  return (
    <ChatPromptSubmitContext.Provider value={{ submitPrompt: handleSubmit }}>
      <div
        className="relative flex h-full min-h-0 w-full min-w-0 overflow-hidden"
        style={
          {
            "--in-app-chat-overlay-width": shouldOverlayChat
              ? `${chatPanelWidthPx}px`
              : "0px",
          } as CSSProperties
        }
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {children}
        </div>

        {open ? (
          <aside
            className={cn(
              "z-30 flex h-full shrink-0 flex-col border-l border-gray-200 bg-white",
              shouldOverlayChat ? "absolute inset-y-0 right-0" : "relative",
              isResizing && "select-none",
            )}
            style={{ width: chatPanelWidthPx }}
            aria-label="New chat"
          >
            <ResizeHandle
              edge="start"
              ariaLabel="Resize chat panel"
              separatorMetrics={{
                valueNow: chatPanelWidthPx,
                valueMin: resizeMinWidthPx,
                valueMax: maxChatPanelWidthPx,
              }}
              onMouseDown={handleResizeMouseDown}
              onKeyDown={handleResizeKeyDown}
              className="z-10"
            />
            <div className="flex shrink-0 items-center justify-between gap-3 px-3 py-3 sm:px-4">
              <p className="text-sm font-medium tracking-[0.14px] text-violet-950">
                New chat
              </p>
              <div className="flex shrink-0 items-center gap-0.5">
                <IconButton
                  icon={<NotePencilIcon className="text-violet-700" />}
                  variant="ghost"
                  size="md"
                  aria-label="Start new chat"
                  tooltipLabel="New chat"
                  tooltipPosition="bottom"
                  onPress={handleNewThread}
                  className="shrink-0 text-violet-950"
                />
                <IconButton
                  icon={<MinusIcon className="text-gray-700" />}
                  variant="ghost"
                  size="md"
                  aria-label="Minimize chat"
                  tooltipLabel="Minimize"
                  tooltipPosition="bottom"
                  onPress={() => setOpen(false)}
                  className="shrink-0"
                />
              </div>
            </div>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3">
                <ChatErrorBoundary>
                  <HeraldChatMessageList
                    messages={messages}
                    isStreaming={isStreaming}
                    showPendingThinking={showPendingThinking}
                    conversationId={capturedConversationId ?? undefined}
                    streamingQuipMessage={streamingQuipMessage}
                    wrapUserBubble
                    className="flex flex-col gap-3"
                  />
                </ChatErrorBoundary>
              </div>

              <ChatErrorBanner
                key={chatErrorBannerKey}
                message={error?.message}
                onRetry={handleBannerRetry}
                isRetryDisabled={isStreaming}
              />

              <ChatInput
                ref={inputRef}
                value={input}
                onChange={setInput}
                onSubmit={handleSubmit}
                onStop={handleStop}
                isStreaming={isStreaming}
                isRecording={isRecording}
                onMicPress={handleMicPress}
                autoFocus={false}
                variant="conversation"
                width="100%"
                minHeight={80}
                placeholder="Ask anything about this page..."
                files={files}
                onFilesChange={setFiles}
                briefingMenu={{
                  mode: isDeepAnalysisEnabled ? "deep" : "briefing",
                  onModeChange: handleBriefingModeChange,
                  isDisabled: isStreaming,
                }}
              />
            </div>
          </aside>
        ) : showOpenChatFab ? (
          createPortal(
            <div className="fixed bottom-6 right-6 z-40 sm:bottom-8 sm:right-8">
              <IconButton
                icon={<HandraiseHGradient className="size-4" />}
                variant="outline"
                size="lg"
                aria-label="Open chat"
                tooltipLabel="Chat about this page"
                tooltipPosition="left"
                onPress={() => setOpen(true)}
                className="rounded-full bg-white shadow-md hover:bg-gray-50 active:bg-gray-100"
              />
            </div>,
            document.body,
          )
        ) : null}
      </div>
    </ChatPromptSubmitContext.Provider>
  );
};

export default InAppChatShell;
